import { Code2, KeyRound, Palette, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { errorMessage, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useCreateEmbedKey, useDeleteEmbedKey, useEmbedKeys, useUpdateEmbedKey } from "../api/queries";
import { InstallSnippets } from "../components/InstallSnippets";
import { OriginsEditor } from "../components/OriginsEditor";
import { Alert, Badge, Button, Card, ChipsInput, CopyButton, Empty, Field, Modal, PageHead, Segmented, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { HelpLink } from "../components/HelpLink";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { normalizeOrigin, originProblem } from "../lib/snippets";

/** "Live" while the calculator was loaded from the website in the last 30 days (`EmbedKey.website_status`). */
const LIVE_DAYS = 30;

/** "3 minutes ago", "yesterday", a date after 30 days, "never": in the reader's language. */
function useRelative(): (value: string | null | undefined) => string {
  const intl = useIntlShape();
  const t = useT();
  return (value) => {
    if (!value) return t("website.time.never");
    const seconds = (Date.now() - new Date(value).getTime()) / 1000;
    if (seconds < 60) return t("website.time.justNow");
    if (seconds < 3600) return intl.formatRelativeTime(-Math.floor(seconds / 60), "minute", { numeric: "auto" });
    if (seconds < 86400) return intl.formatRelativeTime(-Math.floor(seconds / 3600), "hour", { numeric: "auto" });
    if (seconds < 86400 * 30) return intl.formatRelativeTime(-Math.floor(seconds / 86400), "day", { numeric: "auto" });
    return intl.formatDate(value, { year: "numeric", month: "short", day: "numeric" });
  };
}

function WebsiteStatus({ websites }: { websites: S["EmbedKeyWebsite"][] }) {
  const t = useT();
  const relative = useRelative();
  if (!websites.length) return null;
  return (
    <ul className="site-status" aria-label={t("website.status.aria")}>
      {websites.map((w) => {
        const seen = w.last_seen_at ? new Date(w.last_seen_at).getTime() : 0;
        const live = seen > 0 && Date.now() - seen < LIVE_DAYS * 86400_000;
        return (
          <li key={w.origin}>
            <code dir="ltr">{w.origin}</code>
            {live ? (
              <span className="site-chip live">● {t("website.status.live", { when: relative(w.last_seen_at) })}</span>
            ) : (
              <>
                <span className="site-chip waiting">○ {w.last_seen_at ? t("website.status.notSeenSince", { when: relative(w.last_seen_at) }) : t("website.status.notSeenYet")}</span>
                <span className="hint site-hint">{t("website.status.hint")}</span>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
function NewKeyModal({ orgId, onClose, onCreated }: { orgId: string; onClose: () => void; onCreated: (key: S["EmbedKey"]) => void }) {
  const create = useCreateEmbedKey(orgId);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<S["EmbedKeyMode"]>("live");
  const [origins, setOrigins] = useState<string[]>([]);
  const t = useT();
  return (
    <Modal
      title={t("website.newKey.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("website.action.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={() => create.mutate({ label: label || undefined, mode, allowed_origins: origins }, { onSuccess: (k) => onCreated(k) })}
          >
            {t("website.newKey.create")}
          </Button>
        </>
      }
    >
      <div className="stack">
        {create.error && <Alert tone="danger">{errorMessage(create.error)}</Alert>}
        <Field label={t("website.newKey.label.label")} hint={t("website.newKey.label.hint")}>
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("website.newKey.label.placeholder")} />
        </Field>
        <Field label={t("website.newKey.mode.label")} hint={mode === "test" ? t("website.newKey.mode.testHint") : t("website.newKey.mode.liveHint")}>
          <Segmented value={mode} onChange={setMode} options={[{ value: "live", label: t("website.key.mode.live") }, { value: "test", label: t("website.key.mode.test") }]} />
        </Field>
        <Field label={t("website.key.allowedWebsites")}>
          <ChipsInput values={origins} onChange={setOrigins} placeholder={t("website.origins.example")} normalize={normalizeOrigin} validate={(o) => originProblem(o, t)} />
        </Field>
      </div>
    </Modal>
  );
}

export function Widget() {
  const orgId = useOrgId();
  const { can } = useOrg();
  const canManage = can("widget_keys", "edit");
  const keys = useEmbedKeys(orgId);
  const update = useUpdateEmbedKey(orgId);
  const remove = useDeleteEmbedKey(orgId);
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const t = useT();
  const intl = useIntlShape();
  const relative = useRelative();

  if (keys.isLoading) return <Spinner />;
  if (keys.error) return <Alert tone="danger">{errorMessage(keys.error)}</Alert>;
  const items = keys.data?.items ?? [];
  const selected = items.find((k) => k.id === selectedId) ?? items.find((k) => k.active) ?? items[0];

  return (
    <>
      <PageHead
        title={t("website.page.title")}
        description={t("website.page.description")}
        actions={
          <>
            <HelpLink article="website-widget/embed-the-widget/" label={t("website.page.help")} />
            {canManage && (
              <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                {t("website.page.newKey")}
              </Button>
            )}
          </>
        }
      />
      {!items.length ? (
        <div className="stack-lg">
        <Card>
          <Empty icon={<KeyRound />} title={t("website.empty.title")} action={canManage && <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>{t("website.empty.action")}</Button>}>
            {t("website.empty.body")}
          </Empty>
        </Card>
        <Card title={t("website.install.title")} description={t("website.install.description")}>
          <InstallSnippets embedKey={null} onCreateKey={canManage ? () => setCreating(true) : undefined} />
        </Card>
        </div>
      ) : (
        <div className="stack-lg">
          <div className="stack">
            {items.map((k) => (
              <Card key={k.id} className={cx(k.id === selected?.id && "selected")} pad={false}>
                <div className="card-head" style={{ borderBottom: 0, paddingBottom: 6 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <h2>{k.label || t("website.key.untitled")}</h2>
                    <Badge tone={k.mode === "live" ? "ok" : "warn"}>{k.mode === "live" ? t("website.key.badge.live") : t("website.key.badge.test")}</Badge>
                    {!k.active && <Badge tone="danger">{t("website.key.badge.inactive")}</Badge>}
                    <span className="small faint">{t("website.key.lastUsed", { when: relative(k.last_used_at) })}</span>
                  </div>
                  <div className="row">
                    <Button size="sm" variant={k.id === selected?.id ? "primary" : "default"} icon={<Code2 />} aria-pressed={k.id === selected?.id} onClick={() => setSelectedId(k.id)}>
                      {k.id === selected?.id ? t("website.key.codeShown") : t("website.key.showCode")}
                    </Button>
                    {canManage && (
                      <>
                        <Switch
                          checked={k.active}
                          onChange={(active) =>
                            update.mutate({ id: k.id, body: { active } }, { onSuccess: () => toast(active ? t("website.key.activated") : t("website.key.deactivated")), onError: (e) => toast(errorMessage(e), true) })
                          }
                          label={<span className="small muted">{t("website.key.active")}</span>}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 />}
                          aria-label={t("website.key.delete")}
                          onClick={() => {
                            if (window.confirm(t("website.key.deleteConfirm", { name: k.label || k.key })))
                              remove.mutate(k.id, { onSuccess: () => toast(t("website.key.deleted")), onError: (e) => toast(errorMessage(e), true) });
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="card-body grid-2" style={{ paddingTop: 8, alignItems: "start" }}>
                  <Field label={t("website.key.key")}>
                    <div className="keybox">
                      <KeyRound size={16} color="var(--primary)" />
                      <code dir="ltr">{k.key}</code>
                      <CopyButton text={k.key} label={t("website.action.copy")} />
                    </div>
                  </Field>
                  <Field label={t("website.key.allowedWebsites")}>
                    <div className="stack" style={{ gap: 12 }}>
                      <OriginsEditor orgId={orgId} embedKey={k} canManage={canManage} compact />
                      <WebsiteStatus websites={k.website_status} />
                    </div>
                  </Field>
                </div>
              </Card>
            ))}
          </div>

          {selected && (
            <Card title={t("website.code.title")} description={t("website.code.description", { name: selected.label || selected.key })}>
                <InstallSnippets embedKey={selected.key} />
                {!selected.allowed_origins.length && (
                  <div style={{ marginTop: 14 }}>
                    <Alert tone="warn" title={t("website.code.noWebsites.title")}>
                      {t("website.code.noWebsites.body")}
                    </Alert>
                  </div>
                )}
                <p className="hint" style={{ marginTop: 14 }}>
                  <Palette size={12} style={{ verticalAlign: -2 }} />{" "}
                  {intl.formatMessage({ id: "website.code.seeBranding" }, { link: (c) => <Link to="/branding">{c}</Link> })}
                </p>
            </Card>
          )}
        </div>
      )}
      {creating && (
        <NewKeyModal
          orgId={orgId}
          onClose={() => setCreating(false)}
          onCreated={(k) => {
            setCreating(false);
            setSelectedId(k.id);
            toast(t("website.key.created"));
          }}
        />
      )}
    </>
  );
}
